import { useEffect, useState } from "react";
import { Landmark, Pencil, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtOdds, money } from "@/lib/odds";

let BOOKS_CACHE = null;

export function LogBet({ parlay, onChange }) {
  const [edit, setEdit] = useState(false);
  const [books, setBooks] = useState(BOOKS_CACHE || ["DraftKings", "FanDuel", "BetMGM", "Caesars", "Other"]);
  const [book, setBook] = useState(parlay.placed?.book || "DraftKings");
  const [stake, setStake] = useState(parlay.placed?.stake ?? parlay.wager);
  const [odds, setOdds] = useState(parlay.placed?.odds ?? parlay.combined_american);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (BOOKS_CACHE) return;
    api.get("/books").then((r) => { BOOKS_CACHE = r.data.books; setBooks(r.data.books); }).catch(() => {});
  }, []);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/parlays/${parlay.parlay_id}/placed`, { book, stake: Number(stake), odds: Number(odds) });
      onChange(data);
      setEdit(false);
      toast.success(`Logged ${money(data.placed.stake)} at ${fmtOdds(data.placed.odds)} on ${data.placed.book}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    await api.delete(`/parlays/${parlay.parlay_id}/placed`);
    const { placed, real_profit, ...rest } = parlay;
    onChange(rest);
    toast.success("Real bet removed");
  };
  if (!edit && !parlay.placed)
    return <button data-testid="log-bet-btn" onClick={() => setEdit(true)} className="btn-ghost !py-1.5 !px-3 text-xs"><Landmark size={14} /> Log real bet</button>;
  if (!edit)
    return (
      <div data-testid="placed-chip" className="flex flex-wrap items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 text-xs">
        <Landmark size={13} className="text-cyan-400" />
        <span className="text-slate-300">Placed <span className="font-mono-num font-bold text-white" data-testid="placed-stake">{money(parlay.placed.stake)}</span> @ <span className="font-mono-num font-bold text-amber-400" data-testid="placed-odds">{fmtOdds(parlay.placed.odds)}</span> on <span className="font-semibold text-white" data-testid="placed-book">{parlay.placed.book}</span></span>
        {parlay.real_profit != null ? <span className={`font-mono-num font-bold ${parlay.real_profit >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="placed-real-pl">real {parlay.real_profit >= 0 ? "+" : ""}{money(parlay.real_profit)}</span> : <span className="text-slate-500">to win {money(parlay.placed.payout - parlay.placed.stake)}</span>}
        <button data-testid="placed-edit-btn" onClick={() => setEdit(true)} className="rounded p-1 text-slate-400 hover:text-white" aria-label="Edit"><Pencil size={12} /></button>
        <button data-testid="placed-clear-btn" onClick={clear} className="rounded p-1 text-slate-400 hover:text-red-400" aria-label="Remove"><Trash2 size={12} /></button>
      </div>
    );
  return (
    <form data-testid="log-bet-form" onSubmit={save} className="flex flex-wrap items-end gap-2 rounded-lg border border-[#26334D] bg-[#0B0E14]/60 px-3 py-2">
      <label className="text-[10px] uppercase tracking-wider text-slate-500">Book<select data-testid="log-bet-book" value={book} onChange={(e) => setBook(e.target.value)} className="input-dark mt-0.5 block !py-1.5 text-xs">{books.map((b) => <option key={b}>{b}</option>)}</select></label>
      <label className="text-[10px] uppercase tracking-wider text-slate-500">Stake $<input data-testid="log-bet-stake" type="number" min="1" step="1" value={stake} onChange={(e) => setStake(e.target.value)} className="input-dark mt-0.5 block w-24 !py-1.5 text-xs" /></label>
      <label className="text-[10px] uppercase tracking-wider text-slate-500">Odds<input data-testid="log-bet-odds" type="number" step="1" value={odds} onChange={(e) => setOdds(e.target.value)} className="input-dark mt-0.5 block w-24 !py-1.5 text-xs" /></label>
      <button data-testid="log-bet-save" disabled={busy} className="btn-primary !px-3 !py-1.5 text-xs"><Check size={13} /> Save</button>
      <button type="button" data-testid="log-bet-cancel" onClick={() => setEdit(false)} className="text-xs text-slate-500 hover:text-white">Cancel</button>
    </form>
  );
}
